import React, { useState, useEffect } from 'react';
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  ArrowLeftOnRectangleIcon,
  CheckIcon,
  ArrowUpCircleIcon,
} from '@heroicons/react/24/outline';
import { CircularProgress } from '@mui/material';

const SettingsPage = () => {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('account');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');

  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;
  const navigate = useNavigate();

  useEffect(() => {
    axios({
      method: "GET",
      withCredentials: true,
      url: `${backEndUrl}/auth/consultantLog`,
    }).then((res) => {
      setData(res.data);
      setName(res.data.name || '');
      setEmail(res.data.email || '');
      setCompanyName(res.data.companyName || '');
    }).catch(() => {
      navigate('/login');
    });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      await axios({
        method: "PUT",
        withCredentials: true,
        url: `${backEndUrl}/update/profile`,
        data: { name, companyName },
      });
      setData(prev => ({ ...prev, name, email, companyName }));
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setSaveError(err?.response?.data?.message || 'Failed to save. Please try again.');
      setTimeout(() => setSaveError(''), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpgrade = async (subType) => {
    setIsUpgrading(true);
    try {
      const response = await axios({
        method: "POST",
        withCredentials: true,
        url: `${backEndUrl}/users/create-checkout-session`,
        data: {
          subType,
          consultantEmail: data.email,
        },
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setIsUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await axios({
        method: "POST",
        withCredentials: true,
        url: `${backEndUrl}/users/cancel-subscription`,
        data: {
          subscriptionID: data.subscriptionID,
          consultantEmail: data.email,
        },
      });
      if (response.status === 200) {
        setData({ ...data, subscription: 'free' });
        setCancelConfirm(false);
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const handleLogout = async () => {
    await axios({
      method: "POST",
      url: `${backEndUrl}/auth/logout`,
      withCredentials: true,
    });
    window.location.href = '/';
  };

  const tabs = [
    { id: 'account', name: 'My Profile', icon: UserCircleIcon },
    { id: 'company', name: 'Company', icon: BuildingOfficeIcon },
    { id: 'billing', name: 'Subscription', icon: CreditCardIcon },
  ];

  const getPlanLabel = (subscription) => {
    if (subscription === 'free') return 'Free';
    if (subscription === 'paid monthly') return 'Professional';
    if (subscription === 'paid yearly') return 'Professional (Yearly)';
    return subscription;
  };

  const getPlanLimit = (subscription) => {
    if (subscription === 'free') return 'Limit: 1 active client';
    return 'Unlimited active clients · 10 GB storage';
  };

  const isFree = data?.subscription === 'free';

  const renderContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Personal Information</h3>
              <p className="text-sm text-slate-500">Manage your contact details.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 text-left">
                <label className="text-xs font-black uppercase text-slate-400 tracking-wider">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border-0 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all text-slate-800"
                />
              </div>
              <div className="space-y-2 text-left">
                <label className="text-xs font-black uppercase text-slate-400 tracking-wider">Email Address</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-4 py-3 bg-slate-100 border-0 rounded-xl outline-none text-slate-400 cursor-not-allowed select-none"
                />
                <p className="text-xs text-slate-400">Email address cannot be changed. Please contact support if you need help.</p>
              </div>
            </div>
          </div>
        );

      case 'company':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Company Details</h3>
              <p className="text-sm text-slate-500">This name will appear on your client portals.</p>
            </div>
            <div className="space-y-2 text-left">
              <label className="text-xs font-black uppercase text-slate-400 tracking-wider">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border-0 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all text-slate-800"
              />
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Subscription & Billing</h3>
              <p className="text-sm text-slate-500">Manage your plan.</p>
            </div>

            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="relative z-10">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Current Plan</span>
                <h4 className="text-2xl font-black mt-1">{getPlanLabel(data.subscription)}</h4>
                <p className="text-xs text-slate-400 mt-1">{getPlanLimit(data.subscription)}</p>
              </div>
              {isFree && (
                <div className="relative z-10 mt-5 flex justify-center">
                  <button
                    onClick={() => handleUpgrade('monthly')}
                    disabled={isUpgrading}
                    className="bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-xs font-black py-2.5 px-6 rounded-lg flex items-center gap-2 transition-all"
                  >
                    <ArrowUpCircleIcon className="h-4 w-4" />
                    {isUpgrading ? 'Redirecting...' : 'Upgrade Plan'}
                  </button>
                </div>
              )}
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <CreditCardIcon className="h-24 w-24" />
              </div>
            </div>

            {!isFree && (
              <div>
                {!cancelConfirm ? (
                  <button
                    onClick={() => setCancelConfirm(true)}
                    className="w-full text-center text-xs font-bold text-red-500 p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Cancel Subscription
                  </button>
                ) : (
                  <div className="bg-red-50 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-bold text-red-700">Are you sure you want to cancel?</p>
                    <p className="text-xs text-red-500">You will lose access to premium features and be downgraded to the Free plan.</p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCancelSubscription}
                        className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white text-xs font-black rounded-lg transition-colors"
                      >
                        Yes, Cancel
                      </button>
                      <button
                        onClick={() => setCancelConfirm(false)}
                        className="flex-1 py-2 px-4 bg-white text-slate-700 text-xs font-black rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Keep Plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-8">

        {/* Sidebar Navigation */}
        <aside className="w-full md:w-64 shrink-0 space-y-2">
          <div className="mb-8 text-left px-2">
            <h1 className="text-xl font-black text-slate-900">Settings</h1>
            <p className="text-xs text-slate-500">Manage your ImmiSpace account</p>
          </div>

          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-sky-600 shadow-sm'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <tab.icon className={`h-5 w-5 shrink-0 ${activeTab === tab.id ? 'text-sky-500' : 'text-slate-400'}`} />
                {tab.name}
              </button>
            ))}
          </nav>

          <div className="pt-4 mt-4">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm text-red-500 hover:bg-red-50 transition-all"
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5 shrink-0" />
              Log Out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 bg-white rounded-[2rem] shadow-sm overflow-hidden flex flex-col min-w-0">
          <div className="p-8 md:p-12 flex-1">
            {renderContent()}
          </div>

          {activeTab !== 'billing' && (
            <div className="p-6 flex flex-col gap-2">
              {saveError && (
                <p className="text-xs text-red-500 font-semibold text-right">{saveError}</p>
              )}
              <div className="flex justify-between items-center gap-4">
                <p className="text-xs text-slate-400 italic">
                  {data.name} · {data.email}
                </p>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`px-8 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 shrink-0 ${
                    saveSuccess
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60'
                  }`}
                >
                  {saveSuccess ? (
                    <><CheckIcon className="h-4 w-4" /> Saved</>
                  ) : isSaving ? (
                    'Saving...'
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          )}
        </main>

      </div>
    </div>
  );
};

export default SettingsPage;
